import { useMemo } from "react"
import { useSatelliteProvenanceData } from "plugins/Satellite/provenance/useSatelliteProvenanceData"
import type { SatelliteSceneManifestEntry } from "plugins/Satellite/provenance/useSatelliteProvenanceData"
import { overlapColorForIndex } from "plugins/Satellite/overlapColor"

// Only "July 12-18, 2026"-style spans are needed here - no existing @nova/format formatter covers a range.
function formatDateSpan(first: Date, last: Date) {
    const month = (date: Date) => date.toLocaleDateString("en-US", { month: "long" })
    const day = (date: Date) => date.getDate()
    const year = (date: Date) => date.getFullYear()

    if (year(first) !== year(last)) {
        return `${month(first)} ${day(first)}, ${year(first)} - ${month(last)} ${day(last)}, ${year(last)}`
    }
    if (month(first) !== month(last)) {
        return `${month(first)} ${day(first)} - ${month(last)} ${day(last)}, ${year(first)}`
    }
    if (day(first) !== day(last)) {
        return `${month(first)} ${day(first)}-${day(last)}, ${year(first)}`
    }
    return `${month(first)} ${day(first)}, ${year(first)}`
}

// Segments partition the bar by nominal date position, but never shrink below this share of the full width.
const MIN_SEGMENT_COUNT = 10
const SEGMENT_GAP_PX = 2

export const SatelliteProvenanceTimespan = ({
    flightId,
    scene: hoveredScene,
}: {
    flightId: string
    scene: SatelliteSceneManifestEntry | undefined
}) => {
    const { manifest, activeScenes, dateRange } = useSatelliteProvenanceData(flightId)

    const segments = useMemo(() => {
        if (!activeScenes?.length || !dateRange) return undefined
        const sorted = [...activeScenes].sort(
            (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
        )
        const span = dateRange.last.getTime() - dateRange.first.getTime()
        const minWidthPercent = 100 / Math.max(sorted.length, MIN_SEGMENT_COUNT)

        const nominalLeft = sorted.map((scene) =>
            span <= 0
                ? 0
                : ((new Date(scene.datetime).getTime() - dateRange.first.getTime()) / span) * 100
        )

        // Enforcing minWidthPercent can push a segment past its neighbour's position (most visibly the last
        // segment, whose nominal position sits at exactly 100%). Resolved right-to-left so each segment's width
        // is capped by where its right neighbour actually ended up rendering, not that neighbour's raw nominal
        // position - otherwise the two would overlap instead of butting up against each other.
        const left: number[] = new Array(sorted.length)
        const width: number[] = new Array(sorted.length)
        for (let i = sorted.length - 1; i >= 0; i--) {
            const rightBound = i < sorted.length - 1 ? left[i + 1] : 100
            const naturalWidth = Math.max(minWidthPercent, rightBound - nominalLeft[i])
            left[i] = Math.max(0, Math.min(nominalLeft[i], rightBound - minWidthPercent))
            width[i] = Math.min(naturalWidth, rightBound - left[i])
        }

        return sorted.map((scene, i) => {
            // Colors must match satelliteOverlapLayer.ts's per-scene coloring, which is keyed by position in the
            // raw (unfiltered) manifest scene list, not the active/filtered list this bar renders.
            const rawIndex = manifest?.findIndex((s) => s.sceneId === scene.sceneId) ?? -1
            const [r, g, b] = overlapColorForIndex(rawIndex >= 0 ? rawIndex : i)
            return { scene, left: left[i], width: width[i], color: `rgb(${r}, ${g}, ${b})` }
        })
    }, [activeScenes, dateRange, manifest])

    if (!dateRange || !segments) return null

    return (
        <div className="flex flex-col">
            <small className="font-mono text-gray-200 mb-2">
                {formatDateSpan(dateRange.first, dateRange.last)}
            </small>
            <div className="relative w-full" style={{ height: 6 }}>
                {segments.map(({ scene, left, width, color }, i) => {
                    const isHovered = hoveredScene?.sceneId === scene.sceneId
                    return (
                        <div
                            key={scene.sceneId}
                            className="absolute top-0"
                            style={{
                                left: `${left}%`,
                                width: `calc(${width}% - ${SEGMENT_GAP_PX}px)`,
                                height: 6,
                                border: `1px solid ${color}`,
                                backgroundColor: isHovered ? color : "transparent",
                                borderTopLeftRadius: i === 0 ? 3 : 0,
                                borderBottomLeftRadius: i === 0 ? 3 : 0,
                                borderTopRightRadius: i === segments.length - 1 ? 3 : 0,
                                borderBottomRightRadius: i === segments.length - 1 ? 3 : 0,
                            }}
                        />
                    )
                })}
            </div>
        </div>
    )
}
